// =============================================================
//  CentreLayout — Sidebar + Topbar pour l'espace centre (web).
//  Pattern identique à AdminLayout mais avec une navigation
//  adaptée aux centres : Tableau de bord, Mes annonces,
//  Publier, Profil.
// =============================================================

import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  useWindowDimensions,
  Image,
  Modal,
  Pressable,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useAuth } from '../../private/AuthContext';
import { signOut } from 'firebase/auth';
import { auth } from '../../private/firebaseConfig';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  colors,
  spacing,
  SIDEBAR_WIDTH,
  TOPBAR_HEIGHT,
} from '../../admin/web-ui/theme';
import { Avatar, IconButton, Button } from '../../admin/web-ui/kit';

const NAV_ITEMS = [
  { route: 'TabNavigatorCentre', label: 'Tableau de bord', icon: 'view-dashboard-outline' },
  { route: 'CentreScreenCentre2', label: 'Mes annonces', icon: 'bullhorn-outline' },
  { route: 'CreateContentScreen', label: 'Publier', icon: 'plus-circle-outline' },
  { route: 'HistoriqueScreenCentre', label: 'Historique', icon: 'history' },
];

const BOTTOM_NAV_ITEMS = [
  { route: 'EditProfileScreenCentre', label: 'Mon centre', icon: 'school-outline' },
];

function Sidebar({ collapsed, onClose, isMobile }) {
  const navigation = useNavigation();
  const route = useRoute();
  const current = route?.name;
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const goTo = (target) => {
    if (target === current) {
      onClose?.();
      return;
    }
    if (target === 'TabNavigatorCentre') {
      navigation.reset({ index: 0, routes: [{ name: 'TabNavigatorCentre' }] });
    } else {
      navigation.navigate(target);
    }
    onClose?.();
  };

  const renderItem = (item) => {
    const active = item.route === current;
    return (
      <TouchableOpacity
        key={item.route}
        onPress={() => goTo(item.route)}
        activeOpacity={0.85}
        style={[styles.navItem, active && styles.navItemActive]}
      >
        <MaterialCommunityIcons
          name={item.icon}
          size={20}
          color={active ? colors.sidebarTextActive : colors.sidebarText}
        />
        <Text style={[styles.navLabel, active && styles.navLabelActive]} numberOfLines={1}>
          {item.label}
        </Text>
        {active && <View style={styles.activeDot} />}
      </TouchableOpacity>
    );
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      try { await AsyncStorage.multiRemove(['user', 'userType', 'lastAuthData']); } catch {}
      await signOut(auth);
      setLogoutOpen(false);
      navigation.reset({ index: 0, routes: [{ name: 'LoginScreen' }] });
    } catch (e) {
      console.log('logout error', e?.message || e);
      setLoggingOut(false);
    }
  };

  return (
    <View
      style={[
        styles.sidebar,
        isMobile
          ? { ...styles.sidebarMobile, transform: [{ translateX: collapsed ? -SIDEBAR_WIDTH : 0 }] }
          : null,
      ]}
    >
      <View style={styles.sidebarHeader}>
        <Image source={require('../../assets/logo.png')} style={styles.logoImg} resizeMode="contain" />
        <View style={{ flex: 1 }}>
          <Text style={styles.logoText}>MyConcours</Text>
          <Text style={styles.logoSubtext}>Espace Centre</Text>
        </View>
        {isMobile && (
          <TouchableOpacity onPress={onClose} style={{ padding: 4 }}>
            <MaterialCommunityIcons name="close" size={20} color={colors.sidebarText} />
          </TouchableOpacity>
        )}
      </View>

      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingVertical: 12 }} showsVerticalScrollIndicator={false}>
        <Text style={styles.navSection}>GESTION</Text>
        {NAV_ITEMS.map(renderItem)}
        <Text style={[styles.navSection, { marginTop: 18 }]}>COMPTE</Text>
        {BOTTOM_NAV_ITEMS.map(renderItem)}
      </ScrollView>

      <TouchableOpacity style={styles.logoutBtn} onPress={() => setLogoutOpen(true)} activeOpacity={0.85}>
        <MaterialCommunityIcons name="logout" size={18} color={colors.sidebarText} />
        <Text style={[styles.navLabel, { marginLeft: 10 }]}>Déconnexion</Text>
      </TouchableOpacity>

      <Modal visible={logoutOpen} transparent animationType="fade" onRequestClose={() => !loggingOut && setLogoutOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => !loggingOut && setLogoutOpen(false)}>
          <Pressable style={styles.modalCard} onPress={(e) => e.stopPropagation?.()}>
            <View style={styles.modalIconWrap}>
              <MaterialCommunityIcons name="logout" size={28} color={colors.danger} />
            </View>
            <Text style={styles.modalTitle}>Se déconnecter ?</Text>
            <Text style={styles.modalText}>Tu vas être déconnecté de l'espace centre.</Text>
            <View style={styles.modalActions}>
              <Button variant="secondary" onPress={() => setLogoutOpen(false)} disabled={loggingOut}>Annuler</Button>
              <Button variant="danger" icon="logout" loading={loggingOut} onPress={handleLogout}>Se déconnecter</Button>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

function Topbar({ title, subtitle, onOpenMenu, isMobile }) {
  const navigation = useNavigation();
  const { user } = useAuth();
  const displayName = user?.centerName || user?.fullName || user?.email?.split('@')[0] || 'Centre';

  return (
    <View style={styles.topbar}>
      <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1, minWidth: 0, gap: 12 }}>
        {isMobile && (
          <TouchableOpacity onPress={onOpenMenu} style={{ padding: 6 }}>
            <MaterialCommunityIcons name="menu" size={22} color={colors.text} />
          </TouchableOpacity>
        )}
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={styles.topbarTitle} numberOfLines={1}>{title}</Text>
          {!!subtitle && <Text style={styles.topbarSubtitle} numberOfLines={1}>{subtitle}</Text>}
        </View>
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <IconButton icon="bell-outline" onPress={() => navigation.navigate('NotificationsScreen')} />
        <TouchableOpacity
          onPress={() => navigation.navigate('EditProfileScreenCentre')}
          style={styles.profileBtn}
          activeOpacity={0.85}
        >
          <Avatar src={user?.photoURL} name={displayName} size={32} />
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={styles.profileName} numberOfLines={1}>{displayName}</Text>
            <Text style={styles.profileRole}>Centre / École</Text>
          </View>
        </TouchableOpacity>
      </View>
    </View>
  );
}

export default function CentreLayout({ title, subtitle, children, contentPadding = spacing.xl }) {
  const { width } = useWindowDimensions();
  const isMobile = width < 900;
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <View style={styles.root}>
      {!isMobile && <Sidebar isMobile={false} />}
      {isMobile && (
        <>
          {mobileOpen && (
            <TouchableOpacity style={styles.backdrop} activeOpacity={1} onPress={() => setMobileOpen(false)} />
          )}
          <Sidebar isMobile collapsed={!mobileOpen} onClose={() => setMobileOpen(false)} />
        </>
      )}
      <View style={{ flex: 1, minWidth: 0, height: '100vh' }}>
        <Topbar title={title} subtitle={subtitle} isMobile={isMobile} onOpenMenu={() => setMobileOpen(true)} />
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{ padding: contentPadding, paddingBottom: 80 }}
          showsVerticalScrollIndicator={false}
        >
          {children}
        </ScrollView>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: { flexDirection: 'row', backgroundColor: colors.bg, height: '100vh', overflow: 'hidden' },
  sidebar: {
    width: SIDEBAR_WIDTH,
    backgroundColor: colors.sidebarBg,
    paddingTop: spacing.lg,
    paddingBottom: spacing.md,
    height: '100vh',
    position: 'sticky',
    top: 0,
    flexShrink: 0,
  },
  sidebarMobile: {
    position: 'absolute', top: 0, bottom: 0, left: 0, zIndex: 100,
    shadowColor: '#000', shadowOpacity: 0.2, shadowRadius: 20, transition: 'transform 0.25s ease',
  },
  backdrop: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 90 },
  sidebarHeader: {
    flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 16, paddingBottom: 18,
    borderBottomWidth: 1, borderBottomColor: colors.sidebarBorder,
  },
  logoImg: { width: 38, height: 38, borderRadius: 8, backgroundColor: '#fff', padding: 4 },
  logoText: { color: '#fff', fontSize: 15, fontWeight: '700' },
  logoSubtext: { color: 'rgba(255,255,255,0.6)', fontSize: 11, marginTop: 2 },
  navSection: { fontSize: 11, color: 'rgba(255,255,255,0.45)', fontWeight: '700', letterSpacing: 1, paddingHorizontal: 18, marginBottom: 8 },
  navItem: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 10, marginHorizontal: 10, borderRadius: 10, gap: 12, marginBottom: 2 },
  navItemActive: { backgroundColor: colors.sidebarBgHover },
  navLabel: { color: colors.sidebarText, fontSize: 13, fontWeight: '500', flex: 1 },
  navLabelActive: { color: colors.sidebarTextActive, fontWeight: '600' },
  activeDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: colors.primaryLight },
  logoutBtn: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 24, paddingVertical: 14, marginTop: 8, borderTopWidth: 1, borderTopColor: colors.sidebarBorder },

  topbar: {
    height: TOPBAR_HEIGHT, backgroundColor: colors.surface,
    borderBottomWidth: 1, borderBottomColor: colors.border,
    flexDirection: 'row', alignItems: 'center', paddingHorizontal: spacing.xl, gap: 16,
  },
  topbarTitle: { fontSize: 18, fontWeight: '700', color: colors.text, letterSpacing: -0.3 },
  topbarSubtitle: { fontSize: 12, color: colors.textSecondary, marginTop: 2 },
  profileBtn: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 10, maxWidth: 200 },
  profileName: { fontSize: 13, fontWeight: '600', color: colors.text },
  profileRole: { fontSize: 11, color: colors.textMuted, marginTop: 1 },

  modalBackdrop: { flex: 1, backgroundColor: 'rgba(13, 30, 60, 0.55)', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modalCard: { width: '100%', maxWidth: 420, backgroundColor: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 20px 50px rgba(13,30,60,0.25)', alignItems: 'center' },
  modalIconWrap: { width: 56, height: 56, borderRadius: 28, backgroundColor: colors.dangerSoft, alignItems: 'center', justifyContent: 'center', marginBottom: 14 },
  modalTitle: { fontSize: 18, fontWeight: '700', color: colors.text, marginBottom: 6, textAlign: 'center' },
  modalText: { fontSize: 13, color: colors.textSecondary, lineHeight: 19, textAlign: 'center', marginBottom: 20 },
  modalActions: { flexDirection: 'row', gap: 8, justifyContent: 'flex-end', width: '100%' },
});
